import { useCallback, useEffect, useState } from "react";

/**
 * Web Speech API 는 표준 라이브러리 타입에 없다(Chrome 전용 확장).
 * 쓰는 만큼만 좁게 선언한다 — `any` 로 두면 오탈자가 런타임까지 간다.
 */
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  readonly length: number;
  [i: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}
interface SpeechRecognitionEvent {
  results: { length: number; [i: number]: SpeechRecognitionResult };
}
interface SpeechRecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionInstance {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

function getCtor(): SpeechRecognitionCtor | undefined {
  if (typeof window === "undefined") return undefined;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition;
}

/**
 * 인식기는 브라우저에 하나뿐인 자원(마이크)을 잡는다. 컴포넌트가 여럿이어도
 * 살아 있는 인스턴스는 하나여야 하므로 모듈 스코프에 둔다.
 */
let current: SpeechRecognitionInstance | null = null;

export type VoiceStatus = "idle" | "starting" | "listening";

/**
 * `starting` 이 따로 있는 이유 — 첫 사용 시 브라우저 권한 팝업 때문에 `onstart` 가
 * 수 초 뒤에 온다. 그 사이를 `idle` 로 두면 "반응이 없네" 하고 다시 눌러 인식기가
 * 둘이 되고, 같은 말이 두 벌로 들어온 뒤 마이크 표시가 안 꺼진다.
 */
export type VoiceError =
  | "not-supported"
  | "not-allowed"
  | "no-speech"
  | "audio-capture"
  | "network"
  | "unknown";

/** 사용자에게 그대로 보여줄 문장. 무엇이 잘못됐고 어떻게 하면 되는지까지 적는다. */
export const VOICE_ERROR_MESSAGE: Record<VoiceError, string> = {
  "not-supported": "이 브라우저는 음성 입력을 지원하지 않아요. 아래에 입력해 주세요.",
  "not-allowed": "마이크를 쓸 수 없어요. 브라우저 주소창의 자물쇠에서 마이크를 허용해 주세요.",
  "no-speech": "말소리를 못 들었어요. 다시 눌러서 말해 주세요.",
  "audio-capture": "마이크를 찾지 못했어요. 연결을 확인해 주세요.",
  network: "음성 인식 서버에 닿지 못했어요. 아래에 입력해 주세요.",
  unknown: "음성 인식에 실패했어요. 아래에 입력해 주세요.",
};

const ERROR_MAP: Record<string, VoiceError> = {
  "not-allowed": "not-allowed",
  // HTTPS 가 아니거나 정책으로 막힌 경우. 사용자가 할 일은 권한 거부와 같다.
  "service-not-allowed": "not-allowed",
  "no-speech": "no-speech",
  "audio-capture": "audio-capture",
  network: "network",
};

/** `aborted` 는 우리가 끈 것이라 실패가 아니다 — 안내를 띄우면 안 된다. */
function classify(raw: string): VoiceError | null {
  if (raw === "aborted") return null;
  return ERROR_MAP[raw] ?? "unknown";
}

interface VoiceHandlers {
  /**
   * 확정 전 중간 결과도 함께 온다 — `isFinal` 로 가른다.
   *
   * @param alternatives 확정 시 인식기가 내놓은 후보들(1순위부터). 현장명은 일반 어휘에
   *   없는 고유명사라 1순위가 자주 틀리는데, 2·3순위에 맞는 게 들어 있는 경우가 많다.
   *   우리는 정답 목록(현장명)을 알고 있으니 그중 맞는 걸 고를 수 있다.
   */
  onResult?: (transcript: string, isFinal: boolean, alternatives: string[]) => void;
  onError?: (error: VoiceError) => void;
}

export function useVoiceRecognition() {
  const isSupported = !!getCtor();
  const [status, setStatus] = useState<VoiceStatus>("idle");

  // 화면을 떠나면 마이크를 놓는다 — 안 그러면 브라우저 마이크 표시가 계속 켜져 있다.
  useEffect(() => () => current?.abort(), []);

  /*
    `start`·`cancel` 은 참조를 고정한다. 호출부가 effect 의존성으로 쓰므로
    **안정된 참조 자체가 계약**인 자리다 — React Compiler 가 켜져 있어도 예외로 둔다.
  */
  const start = useCallback(
    (handlers: VoiceHandlers = {}) => {
      const Ctor = getCtor();
      if (!Ctor) {
        handlers.onError?.("not-supported");
        return;
      }

      // **시작 전에 이전 것을 끈다.** 안 끄면 모듈 참조만 새것으로 덮이고 앞 인스턴스는
      // 살아남아, 같은 말이 두 벌로 들어오고 마이크 표시가 안 꺼진다.
      current?.abort();

      const instance = new Ctor();
      current = instance;
      instance.lang = "ko-KR";
      // 말하는 동안 글자가 따라 나와야 듣고 있다는 게 보인다. 기본값은 꺼져 있어
      // 확정 전까지 아무것도 안 왔다.
      instance.interimResults = true;
      instance.continuous = false;
      // 후보를 여러 개 받아 우리 현장명과 맞는 것을 고른다. 하나만 받으면
      // 1순위가 틀렸을 때 손쓸 방법이 없다.
      instance.maxAlternatives = 5;

      instance.onstart = () => {
        if (current !== instance) return;
        setStatus("listening");
      };

      instance.onresult = (event) => {
        if (current !== instance) return;
        let transcript = "";
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i]![0]!.transcript;
        }
        const last = event.results[event.results.length - 1]!;

        // 후보는 **마지막 구간**의 것만 쓴다. 앞 구간까지 조합하면 경우의 수가 곱으로
        // 늘고, 틀리는 건 대개 현장명이 들어간 마지막 구간이다.
        const alternatives: string[] = [];
        if (last.isFinal) {
          const head = transcript.slice(0, transcript.length - last[0]!.transcript.length);
          for (let a = 0; a < last.length; a++) alternatives.push(head + last[a]!.transcript);
        }
        handlers.onResult?.(transcript, last.isFinal, alternatives);
      };

      instance.onerror = (event) => {
        if (current !== instance) return;
        const kind = classify(event.error);
        if (kind) handlers.onError?.(kind);
      };

      instance.onend = () => {
        if (current !== instance) return;
        current = null;
        setStatus("idle");
      };

      setStatus("starting");
      try {
        instance.start();
      } catch {
        // 이미 시작된 인스턴스에 start() 를 부르면 InvalidStateError 가 난다.
        if (current === instance) current = null;
        setStatus("idle");
        handlers.onError?.("unknown");
      }
    },
    // 모듈 스코프 인스턴스만 만지므로 의존성이 없다. 참조가 안정돼야 호출부 effect 가
    // 매 렌더 다시 돌지 않는다.
    []
  );

  const cancel = useCallback(() => {
    const instance = current;
    current = null;
    instance?.abort();
    setStatus("idle");
  }, []);

  return { status, isSupported, start, cancel };
}
